import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { Tooltip } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { NavbarVariantEnum } from 'src/types';
import {
  AccountTitle,
  Bottom,
  BottomContainer,
  DividerContainer,
  LogoContainer,
  MobileBurger,
  MobileBurgerContainer,
  NAVBAR_TRANSITION,
  Nav,
  NavHeader,
  NavList,
  NavbarDivider,
  NavbarStyled,
  StyledHeader,
  ToggleNavbar,
  Top,
} from './BaseNavbar.styles';
import { BackToPlatformLink } from './components/BackToPlatformLink';
import { NavbarProvider, useNavbarContext } from './NavbarProvider';

type Props = {
  topHeader?: string;
  topNavList: React.ReactElement;
  bottomHeader?: string;
  bottomNavList: React.ReactElement;
  secondaryDrawer?: React.ReactElement;
  onToggle?: (isOpen: boolean) => void;
  variant: NavbarVariantEnum;
  responsive?: boolean;
};

export const InnerBaseNavbar = ({
  topHeader,
  topNavList,
  bottomHeader,
  bottomNavList,
  onToggle,
  secondaryDrawer,
  variant,
  responsive = true,
}: Props) => {
  const {
    isOpen: open,
    toggleNavbar,
    isSecondaryDrawerOpen,
  } = useNavbarContext();
  const isMobile = useIsMobile();
  const theme = useTheme();

  const [toggleNavTooltip, setToggleNavTooltip] = useState(false);
  const [toggleNavTooltipRoot, setToggleNavTooltipRoot] =
    useState<HTMLElement | null>(null);

  const handleToggleNav = () => {
    toggleNavbar?.();

    if (onToggle) {
      onToggle(!open);
    }
  };

  const getAngleDirection = () => {
    if (open) return 'left';
    if (isSecondaryDrawerOpen && !isMobile) return 'right';
    if (isSecondaryDrawerOpen || isMobile) return '';
    return 'right';
  };

  const managerToggleBgColor = theme.isVaultVariant
    ? theme.atlas.core.color.gray[950]
    : theme.atlas.semantic.color.surface.inverse;

  // Manager variant: 3-lines + arrow SVG icon (inline to allow theme-driven bg rect).
  // Reporter / Public: plain chevron icon.
  const toggleIcon =
    variant === NavbarVariantEnum.MANAGER ? (
      <div
        ref={setToggleNavTooltipRoot}
        onMouseOver={() => setToggleNavTooltip(true)}
        onMouseOut={() => setToggleNavTooltip(false)}
        onClick={handleToggleNav}
        style={{
          cursor: 'pointer',
          transform: open ? '' : 'rotate(180deg)',
          transition: `transform ${NAVBAR_TRANSITION}`,
          paddingTop: '4px',
        }}
      >
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-label={open ? 'Hide menu' : 'Show menu'}
          role="img"
        >
          <rect width="40" height="40" rx="3" fill={managerToggleBgColor} />
          <path
            d="M10.4158 19.9779L13.9558 16.4379C14.142 16.2505 14.2466 15.9971 14.2466 15.7329C14.2466 15.4687 14.142 15.2152 13.9558 15.0279C13.8628 14.9342 13.7522 14.8598 13.6304 14.809C13.5085 14.7582 13.3778 14.7321 13.2458 14.7321C13.1138 14.7321 12.9831 14.7582 12.8612 14.809C12.7394 14.8598 12.6288 14.9342 12.5358 15.0279L8.2958 19.2679C8.20207 19.3608 8.12768 19.4714 8.07691 19.5933C8.02614 19.7152 8 19.8459 8 19.9779C8 20.1099 8.02614 20.2406 8.07691 20.3625C8.12768 20.4843 8.20207 20.5949 8.2958 20.6879L12.5358 24.9779C12.6292 25.0706 12.7401 25.1439 12.8619 25.1937C12.9837 25.2434 13.1142 25.2686 13.2458 25.2679C13.3774 25.2686 13.5079 25.2434 13.6297 25.1937C13.7515 25.1439 13.8624 25.0706 13.9558 24.9779C14.142 24.7905 14.2466 24.5371 14.2466 24.2729C14.2466 24.0087 14.142 23.7552 13.9558 23.5679L10.4158 19.9779Z"
            fill="white"
          />
          <path
            d="M19.8966 16H31.5966C31.769 16 31.9343 15.8946 32.0562 15.7071C32.1781 15.5196 32.2466 15.2652 32.2466 15C32.2466 14.7348 32.1781 14.4804 32.0562 14.2929C31.9343 14.1054 31.769 14 31.5966 14H19.8966C19.7242 14 19.5589 14.1054 19.437 14.2929C19.3151 14.4804 19.2466 14.7348 19.2466 15C19.2466 15.2652 19.3151 15.5196 19.437 15.7071C19.5589 15.8946 19.7242 16 19.8966 16ZM31.5966 24H19.8966C19.7242 24 19.5589 24.1054 19.437 24.2929C19.3151 24.4804 19.2466 24.7348 19.2466 25C19.2466 25.2652 19.3151 25.5196 19.437 25.7071C19.5589 25.8946 19.7242 26 19.8966 26H31.5966C31.769 26 31.9343 25.8946 32.0562 25.7071C32.1781 25.5196 32.2466 25.2652 32.2466 25C32.2466 24.7348 32.1781 24.4804 32.0562 24.2929C31.9343 24.1054 31.769 24 31.5966 24ZM31.5966 19H19.8966C19.7242 19 19.5589 19.1054 19.437 19.2929C19.3151 19.4804 19.2466 19.7348 19.2466 20C19.2466 20.2652 19.3151 20.5196 19.437 20.7071C19.5589 20.8946 19.7242 21 19.8966 21H31.5966C31.769 21 31.9343 20.8946 32.0562 20.7071C32.1781 20.5196 32.2466 20.2652 32.2466 20C32.2466 19.7348 32.1781 19.4804 32.0562 19.2929C31.9343 19.1054 31.769 19 31.5966 19Z"
            fill="white"
          />
        </svg>
      </div>
    ) : (
      <StyledIcon
        className={`uil uil-angle-${getAngleDirection()}`}
        size="32px"
        ref={setToggleNavTooltipRoot}
        onMouseOver={() => setToggleNavTooltip(true)}
        onMouseOut={() => setToggleNavTooltip(false)}
        onClick={handleToggleNav}
      />
    );

  return (
    <>
      <NavbarStyled
        variant={variant}
        open={open}
        responsive={responsive}
        secondaryDrawer={isSecondaryDrawerOpen}
      >
        <Top open={open}>
          {open && (
            <LogoContainer>
              <img
                src="/assets/Diligent-favicon.svg"
                alt="Diligent"
                width={25}
                height={25}
              />
              <StyledHeader>Resolution Hub</StyledHeader>
            </LogoContainer>
          )}
          <ToggleNavbar open={open} variant={variant}>
            {toggleIcon}
            {toggleNavTooltip && (
              <Tooltip
                text={open ? 'Hide menu' : 'Show menu'}
                variant="left"
                root={toggleNavTooltipRoot as HTMLElement}
              />
            )}
          </ToggleNavbar>
        </Top>
        <DividerContainer>
          <NavbarDivider variant={'horizontal'} navbarVariant={variant} />
        </DividerContainer>
        {variant === NavbarVariantEnum.MANAGER && (
          <BackToPlatformLink navOpen={open} />
        )}
        <Nav>
          {topHeader && (
            <NavHeader variant={variant} navOpen={open}>
              {topHeader}
            </NavHeader>
          )}
          <NavList>{topNavList}</NavList>
        </Nav>

        <BottomContainer variant={variant}>
          <Bottom variant={variant}>
            {bottomHeader && (
              <AccountTitle variant={variant} open={open}>
                {bottomHeader}
              </AccountTitle>
            )}
            {bottomNavList}
          </Bottom>
        </BottomContainer>

        {secondaryDrawer}
      </NavbarStyled>
      {responsive && (
        <MobileBurgerContainer open={open} variant={variant}>
          <MobileBurger
            color={
              theme.isVaultVariant
                ? theme.newPalette.neutral.N0
                : theme.atlas.core.color.gray[950]
            }
            className="uil-bars"
            onClick={toggleNavbar}
            size="32px"
          />
        </MobileBurgerContainer>
      )}
    </>
  );
};

export const BaseNavbar = (props: Props) => {
  return (
    <NavbarProvider responsive={props.responsive}>
      <InnerBaseNavbar {...props} />
    </NavbarProvider>
  );
};
